import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, deleteDoc, addDoc } from "firebase/firestore";
import { deleteUser } from "firebase/auth";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import { translations } from './Translation';

// --- IMAGE COMPRESSOR HELPER ---
const compressImage = (file, maxWidth, maxHeight, quality) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality)); 
      };
    };
  });
};

function PartnerDashboard({ user, onNavigate }) {
  const [language, setLanguage] = useState(localStorage.getItem('elitecut_lang') || 'en');
  const t = translations[language] || translations.en;
  const [addressInput, setAddressInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Add this effect so the dashboard updates instantly when the language button is clicked
  useEffect(() => {
    const handleStorage = () => setLanguage(localStorage.getItem('elitecut_lang') || 'en');
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);


  const [activeTab, setActiveTab] = useState('dashboard');
  const handleShareToClients = async () => {
    const shareText = `Book your next haircut with us on BookMyStyle! Search our shop or use our code: ${user.uid}`;
    const shareData = {
      title: 'Book with us on BookMyStyle',
      text: shareText,
      url: window.location.origin
    };
    
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (err) { console.log('Share cancelled', err); }
    } else {
      navigator.clipboard.writeText(`${shareText} | Link: ${window.location.origin}`);
      alert("Invite code and link copied to clipboard! You can now paste it in Instagram or WhatsApp.");
    }
  };
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [shopData, setShopData] = useState(null);
  
  // Team State
  const [barbers, setBarbers] = useState([]);
  const [newBarberName, setNewBarberName] = useState('');
  const [newBarberImage, setNewBarberImage] = useState(''); 

  // Shop Settings State
  const [shopAddress, setShopAddress] = useState('');
  const [shopLat, setShopLat] = useState(null);
  const [shopLng, setShopLng] = useState(null);
  const [shopImage, setShopImage] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  
  // NEW: Vibe Tags State
  const [shopVibes, setShopVibes] = useState([]);
  const AVAILABLE_VIBES = ['🎮 PS5 Lounge', '🍻 Free Beer', '🎧 Hip-Hop', '🤫 Quiet & Relaxing', '✂️ Old School', '📺 Live Sports'];

  // Service Modal
  // Service Modal States
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedBarberId, setSelectedBarberId] = useState(null);
  const [newService, setNewService] = useState({ name: '', price: '', duration: '' });
  const [editingServiceIndex, setEditingServiceIndex] = useState(null); // <-- NEW: Tracks which service is being edited

  // Analytics State
  const [stats, setStats] = useState({ revenue: 0, clients: 0 });
  const [revenueData, setRevenueData] = useState([]);
  const [barberStats, setBarberStats] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]); // <-- NEW: Stores all shop bookings

  // --- NEW: Marketing & Calendar State ---
  const [isCalendarConnected, setIsCalendarConnected] = useState(false);
  const shopBookingLink = `https://elitecut.com/book/${user?.uid}`;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(shopBookingLink)}`;

  
  const [shopName, setShopName] = useState('');
  const [schedule, setSchedule] = useState({
    Sunday: { open: '09:00', close: '18:00', closed: true },
    Monday: { open: '09:00', close: '18:00', closed: false },
    Tuesday: { open: '09:00', close: '18:00', closed: false },
    Wednesday: { open: '09:00', close: '18:00', closed: false },
    Thursday: { open: '09:00', close: '18:00', closed: false },
    Friday: { open: '09:00', close: '18:00', closed: false },
    Saturday: { open: '09:00', close: '18:00', closed: false },
  });

  // REPLACED EFFECT (Put this around line 54)
  useEffect(() => {
    // If input is too short, or if we already have coordinates (meaning they just clicked a suggestion), don't search.
    if (shopAddress.length < 3 || (shopLat && shopLng)) {
      setAddressSuggestions([]);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      try {
        const safeSearch = encodeURIComponent(shopAddress);
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${safeSearch}&addressdetails=1&limit=5&countrycodes=it`);
        const data = await res.json();
        setAddressSuggestions(data); // Save to your existing addressSuggestions state
      } catch (err) {
        console.error("Autocomplete error:", err);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [shopAddress]); // Watches shopAddress instead
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      // 1. Fetch Shop Settings
      const shopSnap = await getDoc(doc(db, "shops", user.uid));
      if (shopSnap.exists()) {
        const data = shopSnap.data();
        setShopData(data);
        if (data.barbers) setBarbers(data.barbers);
        setShopAddress(data.address || '');
        setShopLat(data.lat || null);
        setShopLng(data.lng || null);
        setShopImage(data.shopImage || '');
        setShopVibes(data.vibes || []); // Load vibes

        if (data.shopName) setShopName(data.shopName);
        if (data.schedule) setSchedule(data.schedule);
      }

      // 2. Fetch Appointments for Analytics
      const q = query(collection(db, "appointments"), where("shopId", "==", user.uid));
      const appSnap = await getDocs(q);
      const apps = appSnap.docs.map(d => d.data());

      let totalRev = 0;
      const dailyRev = {};
      const barberEarns = {};

      apps.forEach(app => {
        const priceStr = app.price || "0";
        const num = parseFloat(priceStr.replace(/[^\d.-]/g, '')) || 0;
        totalRev += num;

        const date = app.date; 
        if (!dailyRev[date]) dailyRev[date] = 0;
        dailyRev[date] += num;

        const bName = app.barberName || "Unknown";
        if (!barberEarns[bName]) barberEarns[bName] = 0;
        barberEarns[bName] += num;
      });

      const formattedRevData = Object.keys(dailyRev).sort().map(date => ({
        date: date.substring(5),
        revenue: dailyRev[date]
      }));

      const formattedBarberData = Object.keys(barberEarns).map(name => ({
        name,
        earnings: barberEarns[name]
      }));

      setStats({ revenue: totalRev, clients: apps.length });
      setRevenueData(formattedRevData);
      setBarberStats(formattedBarberData);
      setAllAppointments(apps); // <-- NEW: Save bookings so the owner can see them
    };
    fetchData();
  }, [user]);

  // --- LIVE ADDRESS SEARCH (FREE OSM API) ---
  // DELETE THESE LINES (Around line 124-135):
  const handleAddressSearch = async (query) => {
    setShopAddress(query);
    if (query.length > 3) {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=5`, {
          headers: { "Accept-Language": "en-US,en;q=0.9" }
        });
        const data = await res.json();
        setAddressSuggestions(data);
      } catch (err) { console.error("Map fetch error", err); }
    } else {
      setAddressSuggestions([]);
    }
  };

  const selectAddress = (addr) => {
    setShopAddress(addr.display_name);
    setShopLat(addr.lat);
    setShopLng(addr.lon);
    setAddressSuggestions([]);
  };

  // UPDATED: Now uses compression
  const handleImageUpload = async (e, callback) => {
    const file = e.target.files[0];
    if (file) {
      const compressedBase64 = await compressImage(file, 800, 800, 0.7);
      callback(compressedBase64);
    }
  };

  const saveBarbersToDb = async (updatedBarbers) => {
    try {
      await updateDoc(doc(db, "shops", user.uid), { barbers: updatedBarbers });
      setBarbers(updatedBarbers); 
    } catch (err) { alert("Error saving: " + err.message); }
  };

  const handleAddBarber = () => {
    if (!newBarberName) return;
    const newBarber = { id: Date.now(), name: newBarberName, role: 'Barber', services: [], image: newBarberImage, portfolio: [] };
    saveBarbersToDb([...barbers, newBarber]);
    setNewBarberName('');
    setNewBarberImage('');
  };

  const updateBarberPhoto = (barberId, newImageBase64) => {
    const updatedBarbers = barbers.map(b => b.id === barberId ? { ...b, image: newImageBase64 } : b);
    saveBarbersToDb(updatedBarbers);
  };

  // --- NEW: Link Profile to Owner ---
  const handleSetAsOwner = async (barberId) => {
    try {
      await updateDoc(doc(db, "shops", user.uid), { ownerBarberId: barberId });
      setShopData({ ...shopData, ownerBarberId: barberId });
      alert("Success! This profile is now linked to your owner account.");
    } catch (err) { alert("Error linking profile: " + err.message); }
  };

  const handleDeleteShop = async () => {
    const confirmMessage = "⚠️ WARNING ⚠️\nAre you sure you want to permanently delete your shop, all barbers, and your account? This action CANNOT be undone.";
    if (window.confirm(confirmMessage)) {
      try {
        await deleteDoc(doc(db, "shops", user.uid));
        await deleteDoc(doc(db, "users", user.uid));
        await deleteUser(auth.currentUser);
        alert("Your shop and account have been successfully deleted.");
      } catch (err) {
        alert("Error deleting account: " + err.message + "\n(Note: For security reasons, you may need to log out and log back in before deleting your account.)");
      }
    }
  };

  // UPDATED: Now saves the vibes to Firestore
  const saveShopSettings = async (e) => {
    e.preventDefault();
    try {
      await updateDoc(doc(db, "shops", user.uid), { 
        shopName: shopName,          // <-- ADDED
        schedule: schedule,          // <-- ADDED
        address: shopAddress, 
        lat: shopLat,
        lng: shopLng,
        shopImage: shopImage,
        vibes: shopVibes 
      });
      alert("Shop Settings Saved! Your profile is updated.");
    } catch (err) { alert("Error saving settings."); }
  };

  const handleEditBarberName = (barberId, currentName) => {
    const newName = window.prompt("Enter new name for the barber:", currentName);
    if (newName && newName.trim() !== "") {
      const updatedBarbers = barbers.map(b => b.id === barberId ? { ...b, name: newName.trim() } : b);
      saveBarbersToDb(updatedBarbers);
    }
  };

  const openServiceModal = (barberId, serviceIndex = null) => {
    setSelectedBarberId(barberId);
    if (serviceIndex !== null) {
      // Edit Mode
      const serviceToEdit = barbers.find(b => b.id === barberId).services[serviceIndex];
      setNewService(serviceToEdit);
      setEditingServiceIndex(serviceIndex);
    } else {
      // Add Mode
      setNewService({ name: '', price: '', duration: '' });
      setEditingServiceIndex(null);
    }
    setIsServiceModalOpen(true);
  };
  const handleSaveService = (e) => {
    e.preventDefault();
    if (!newService.name || !newService.price) return;
    
    const updatedBarbers = barbers.map(b => {
      if (b.id === selectedBarberId) {
        let updatedServices = [...(b.services || [])];
        if (editingServiceIndex !== null) {
          updatedServices[editingServiceIndex] = newService; // Update existing
        } else {
          updatedServices.push(newService); // Add new
        }
        return { ...b, services: updatedServices };
      }
      return b;
    });
    
    saveBarbersToDb(updatedBarbers);
    setIsServiceModalOpen(false);
    setNewService({ name: '', price: '', duration: '' }); 
    setEditingServiceIndex(null);
  };

  const handleDeleteService = (barberId, serviceIndex) => {
    if(window.confirm("Are you sure you want to delete this service?")) {
      const updatedBarbers = barbers.map(b => {
        if (b.id === barberId) {
          const newServices = b.services.filter((_, i) => i !== serviceIndex);
          return { ...b, services: newServices };
        }
        return b;
      });
      saveBarbersToDb(updatedBarbers);
    }
  };
  // UPDATED: Now uses compressImage and limits to 15
  const handlePortfolioUpload = (barberId, e) => {
    handleImageUpload(e, (base64) => {
      const updatedBarbers = barbers.map(b => {
        if (b.id === barberId) {
          const currentPortfolio = b.portfolio || [];
         return { ...b, portfolio: [...currentPortfolio, base64].slice(0, 15) };
        }
        return b;
      });
      saveBarbersToDb(updatedBarbers);
      alert("Portfolio photo added!");
    });
  };

  const handleDeletePortfolioPhoto = (barberId, indexToRemove) => {
    if(window.confirm("Delete this photo?")) {
      const updatedBarbers = barbers.map(b => {
        if (b.id === barberId) {
          const newPortfolio = b.portfolio.filter((_, i) => i !== indexToRemove);
          return { ...b, portfolio: newPortfolio };
        }
        return b;
      });
      saveBarbersToDb(updatedBarbers);
    }
  };

  return (
    <div className="dashboard-container">
     {/* 1. MOBILE TOGGLE BUTTON */}
  <button 
    className="mobile-menu-toggle" 
    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
  >
    ☰
  </button>

  {/* 2. MOBILE OVERLAY (Closes menu when tapping outside) */}
  {isSidebarOpen && (
    <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>
  )}

  {/* 3. UPDATED SIDEBAR (Notice the dynamic class and the added setIsSidebarOpen(false) on clicks) */}
  <aside className={`dash-sidebar ${isSidebarOpen ? 'open' : ''}`}>
    <div 
      className="dash-logo" 
      onClick={() => { onNavigate('home'); setIsSidebarOpen(false); }} 
      style={{ cursor: 'pointer', transition: '0.2s' }} 
      title="Back to Home Page"
    >
      💈 Back to Home
    </div>
    <nav>
      <button className={activeTab === 'bookings' ? 'active' : ''} onClick={() => { setActiveTab('bookings'); setIsSidebarOpen(false); }}>{t.myBookings}</button>
      <button className={activeTab === 'favorites' ? 'active' : ''} onClick={() => { setActiveTab('favorites'); setIsSidebarOpen(false); }}>{t.favShops}</button>
      <button className={activeTab === 'settings' ? 'active' : ''} onClick={() => { setActiveTab('settings'); setIsSidebarOpen(false); }}>{t.settings}</button>
      <button className={activeTab === 'team' ? 'active' : ''} onClick={() => { setActiveTab('team'); setIsSidebarOpen(false); }}>{t.teamAndServices}</button>
      <button onClick={() => { auth.signOut(); setIsSidebarOpen(false); }} style={{marginTop: 'auto', color: '#ef4444'}}>🚪 Logout</button>
    </nav>
  </aside>
      <main className="dash-content">
        {/* --- NEW: PERSISTENT SYNC PROMPT --- */}
        {!isCalendarConnected && (
          <div style={{ 
            background: 'linear-gradient(90deg, #fffbeb, #fef3c7)', 
            border: '1px solid #fcd34d', 
            padding: '12px 20px', 
            borderRadius: '12px', 
            marginBottom: '20px', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
              <div>
                <strong style={{ color: '#92400e', fontSize: '14px' }}>Calendar Not Synced</strong>
                <p style={{ margin: 0, color: '#b45309', fontSize: '12px' }}>{t.calendarNotSyncedDesc}</p>
              </div>
            </div>
            <button 
              onClick={() => setActiveTab('dashboard')} // Scrolls them to the sync card
              style={{ background: '#f59e0b', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}
            >
              Fix Now
            </button>
          </div>
        )}

        <header className="dash-header">
          <div>
            <h1>{t.shopAdmin}</h1>
            <p>{shopData?.shopName || t.welcomeBack}, {user.name}!</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="outline-btn" onClick={handleShareToClients} style={{ background: '#ec4899', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {t.shareToClients}
            </button>
            <button className="premium-book-btn" onClick={() => onNavigate('home')}>
              {t.viewLivePage}
            </button>
          </div>
        </header>

        {/* =========================================
            DASHBOARD & STATS TAB
            ========================================= */}
        {activeTab === 'dashboard' && (
          <div className="admin-section" style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }}>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
  <div style={{ background: '#eff6ff', padding: '25px', borderRadius: '16px', border: '1px solid #bfdbfe' }}>
    <h3 style={{ margin: '0 0 10px 0', color: '#1e3a8a', fontSize: '16px' }}>{t.revenueGenerated}</h3>
    <p style={{ margin: 0, fontSize: '36px', fontWeight: '800', color: '#0f172a' }}>{stats.revenue.toLocaleString()} ALL</p>
  </div>
  <div style={{ background: '#f0fdf4', padding: '25px', borderRadius: '16px', border: '1px solid #bbf7d0' }}>
    <h3 style={{ margin: '0 0 10px 0', color: '#14532d', fontSize: '16px' }}>{t.totalClients}</h3>
    <p style={{ margin: 0, fontSize: '36px', fontWeight: '800', color: '#0f172a' }}>{stats.clients}</p>
  </div>
</div>

<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
  <div style={{ background: 'white', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
    <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#0f172a' }}>{t.revenueTrend}</h3>
    <div style={{ width: '100%', height: 250 }}>
      {revenueData.length > 0 ? (
        <ResponsiveContainer>
          <LineChart data={revenueData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0"/>
            <XAxis dataKey="date" tick={{fontSize: 12, fill: '#64748b'}} axisLine={false} tickLine={false} />
            <YAxis tick={{fontSize: 12, fill: '#64748b'}} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 10px rgba(0,0,0,0.1)'}} />
            <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={3} dot={{r: 4}} activeDot={{r: 6}} />
          </LineChart>
        </ResponsiveContainer>
      ) : <p style={{color: '#64748b', textAlign: 'center', marginTop: '100px'}}>{t.noRevenueData}</p>}
    </div>
  </div>
  
  <div style={{ background: 'white', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
    <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#0f172a' }}>{t.topEarning}</h3>
    <div style={{ width: '100%', height: 250 }}>
      {barberStats.length > 0 ? (
        <ResponsiveContainer>
          <BarChart data={barberStats}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0"/>
            <XAxis dataKey="name" tick={{fontSize: 12, fill: '#64748b'}} axisLine={false} tickLine={false} />
            <YAxis tick={{fontSize: 12, fill: '#64748b'}} axisLine={false} tickLine={false} />
            <Tooltip cursor={{fill: '#f1f5f9'}} contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 10px rgba(0,0,0,0.1)'}} />
            <Bar dataKey="earnings" fill="#10b981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      ) : <p style={{color: '#64748b', textAlign: 'center', marginTop: '100px'}}>{t.noBarberData}</p>}
    </div>
         </div>
            </div>

            {/* --- NEW: MARKETING & INTEGRATIONS SECTION --- */}
            <div style={{ marginTop: '30px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              
  {/* Marketing QR Code Card */}
        <div style={{ background: 'white', padding: '25px', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '20px' }}>
          <img src={qrCodeUrl} alt="Shop QR Code" style={{ width: '120px', height: '120px', borderRadius: '8px', border: '1px solid #cbd5e1', padding: '5px' }} />
          <div>
            <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', color: '#0f172a' }}>{t.shopQrCode}</h3>
            <p style={{ margin: '0 0 15px 0', fontSize: '13px', color: '#64748b' }}>{t.shopQrDesc}</p>
            <button 
              onClick={() => { navigator.clipboard.writeText(shopBookingLink); alert(t.linkCopied); }}
              style={{ background: '#f1f5f9', color: '#3b82f6', border: 'none', padding: '8px 15px', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}
            >
              {t.copyDirectLink}
            </button>
          </div>
        </div>

  {/* Google Calendar Sync Card */}
        <div style={{ background: 'white', padding: '25px', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <img src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg" alt="Google Calendar" style={{ width: '24px' }} />
            <h3 style={{ margin: 0, fontSize: '16px', color: '#0f172a' }}>{t.googleCalSync}</h3>
          </div>
          <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#64748b' }}>{t.googleCalDesc}</p>
          <button 
            onClick={() => {
              setIsCalendarConnected(!isCalendarConnected);
              if (!isCalendarConnected) alert("Redirecting to Google Authorization...");
            }}
            style={{ background: isCalendarConnected ? '#10b981' : '#0f172a', color: 'white', border: 'none', padding: '12px 15px', borderRadius: '8px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', transition: '0.2s' }}
          >
            {isCalendarConnected ? t.connectedGoogle : t.connectCalendar}
          </button>
        </div>

</div>
          </div>
        )}
        {/* =========================================
            SETTINGS TAB
            ========================================= */}
        {activeTab === 'settings' && (
          <div className="admin-section">
            <div style={{ background: '#eff6ff', padding: '20px', borderRadius: '12px', marginBottom: '30px', border: '1px solid #bfdbfe' }}>
              <h3 style={{ color: '#1e3a8a', margin: '0 0 10px 0' }}>Staff Invite Code</h3>
              <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Give this code to your barbers when they register.</p>
              <code style={{ background: 'white', padding: '10px 15px', borderRadius: '8px', fontSize: '18px', fontWeight: 'bold' }}>{user.uid}</code>
            </div>

            <h2>Shop Profile Setup</h2>
            <form onSubmit={saveShopSettings} style={{maxWidth: '500px'}}>
              {/* SHOP NAME INPUT */}
              <div className="input-group" style={{ marginBottom: '20px' }}>
                <label>Shop Name</label>
                <input 
                  type="text" 
                  value={shopName}
                  onChange={(e) => setShopName(e.target.value)}
                  placeholder="Enter your barbershop's name" 
                  style={{ padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', width: '100%', boxSizing: 'border-box' }} 
                />
              </div>

              {/* WORKING HOURS INPUT */}
              <div className="input-group" style={{ marginBottom: '30px' }}>
                <label>Working Hours</label>
                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  {Object.keys(schedule).map(day => (
                    <div key={day} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: day !== 'Saturday' ? '1px solid #cbd5e1' : 'none' }}>
                      
                      {/* Day Name & Closed Checkbox */}
                      <div style={{ display: 'flex', alignItems: 'center', width: '120px' }}>
                        <input 
                          type="checkbox" 
                          checked={schedule[day].closed} 
                          onChange={(e) => setSchedule({...schedule, [day]: {...schedule[day], closed: e.target.checked}})}
                          style={{ marginRight: '8px', cursor: 'pointer' }}
                        />
                        <span style={{ fontWeight: 'bold', color: schedule[day].closed ? '#94a3b8' : '#0f172a' }}>{day}</span>
                      </div>

                      {/* Time Selectors (Hidden if closed) */}
                      {!schedule[day].closed ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <input 
                            type="time" 
                            value={schedule[day].open} 
                            onChange={(e) => setSchedule({...schedule, [day]: {...schedule[day], open: e.target.value}})}
                            style={{ padding: '5px', borderRadius: '5px', border: '1px solid #cbd5e1', outline: 'none' }}
                          />
                          <span style={{ fontSize: '12px', color: '#64748b' }}>to</span>
                          <input 
                            type="time" 
                            value={schedule[day].close} 
                            onChange={(e) => setSchedule({...schedule, [day]: {...schedule[day], close: e.target.value}})}
                            style={{ padding: '5px', borderRadius: '5px', border: '1px solid #cbd5e1', outline: 'none' }}
                          />
                        </div>
                      ) : (
                        <span style={{ color: '#ef4444', fontSize: '13px', fontWeight: 'bold' }}>Closed</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="input-group autocomplete-wrapper" style={{marginBottom: '20px', position: 'relative'}}>
                <label>Real Map Address (Required for Pins)</label>
                <input 
                  type="text" value={shopAddress}onChange={(e) => {
                    setShopAddress(e.target.value);
                    setShopLat(null); // Clear old coordinates so the search triggers again
                    setShopLng(null);
                  }}
                  placeholder="Start typing real address..." 
                  style={{padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', width: '100%', boxSizing: 'border-box'}} 
                />
                {addressSuggestions.length > 0 && (
                  <ul className="autocomplete-dropdown">
                    {addressSuggestions.map((suggestion, idx) => (
                      <li key={idx} onClick={() => selectAddress(suggestion)}>📍 {suggestion.display_name}</li>
                    ))}
                  </ul>
                )}
              </div>
              
              <div className="input-group" style={{marginBottom: '20px'}}>
                <label>Shop Cover Photo</label>
                {shopImage && <img src={shopImage} alt="Shop Cover" style={{width: '100%', height: '150px', objectFit: 'cover', borderRadius: '12px', marginBottom: '10px'}} />}
                <label className="custom-file-upload">
                  <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, setShopImage)} />
                  {shopImage ? 'Change Photo' : 'Upload Cover Photo'}
                </label>
              </div>

              {/* NEW: Vibe Tags Selection */}
              <div className="input-group" style={{marginBottom: '30px'}}>
                <label>Shop Vibes (Select up to 3)</label>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px'}}>
                  {AVAILABLE_VIBES.map(vibe => (
                    <div 
                      key={vibe} 
                      onClick={() => {
                        if (shopVibes.includes(vibe)) setShopVibes(shopVibes.filter(v => v !== vibe));
                        else if (shopVibes.length < 3) setShopVibes([...shopVibes, vibe]);
                      }}
                      style={{ padding: '8px 15px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', border: '1px solid #cbd5e1', background: shopVibes.includes(vibe) ? '#0f172a' : 'white', color: shopVibes.includes(vibe) ? 'white' : '#64748b', transition: '0.2s' }}
                    >
                      {vibe}
                    </div>
                  ))}
                </div>
              </div>

              <button type="submit" className="premium-book-btn">Save Shop Profile</button>
              
              <div style={{ marginTop: '50px', paddingTop: '30px', borderTop: '1px solid #e2e8f0' }}>
                <h3 style={{ color: '#ef4444', margin: '0 0 10px 0' }}>Danger Zone</h3>
                <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '15px' }}>Permanently delete your shop, all scheduled appointments, and your account.</p>
                <button type="button" onClick={handleDeleteShop} style={{ background: 'white', color: '#ef4444', border: '1px solid #ef4444', padding: '10px 20px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
                  Delete My Shop
                </button>
              </div>
            </form>
          </div>
        )}

        {/* =========================================
            TEAM TAB
            ========================================= */}
        {activeTab === 'team' && (
          <div className="admin-section">
            <h2>Add New Barber</h2>
            <div className="add-barber-row" style={{alignItems: 'center'}}>
              <label className="custom-file-upload" style={{marginRight: '10px'}}>
                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, setNewBarberImage)} />
                {newBarberImage ? t.photoAttached : t.addPhoto}
              </label>
              <input type="text" placeholder="Barber Name..." value={newBarberName} onChange={(e) => setNewBarberName(e.target.value)} />
              <button className="premium-login-btn" onClick={handleAddBarber}>+ Add Barber</button>
            </div>

           <h2 style={{marginTop: '40px', borderTop: '1px solid #f1f5f9', paddingTop: '20px'}}>{t.currentTeam}</h2>
            <div className="barbers-list">
              {barbers.length === 0 && <p className="empty-text">No barbers added yet.</p>}
              {barbers.map(barber => (
                <div key={barber.id} className="admin-barber-card">
                  <div className="barber-header-admin" style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                    <div style={{textAlign: 'center'}}>
                      {barber.image ? <img src={barber.image} alt={barber.name} style={{width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover'}} /> : <div style={{fontSize: '30px'}}>👤</div>}
                      <label className="custom-file-upload" style={{fontSize: '10px', padding: '4px 8px', marginTop: '5px', display: 'block'}}>
                        <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, (base64) => updateBarberPhoto(barber.id, base64))} /> 
                        {t.updatePhoto}
                      </label>
                    </div>
                 <div style={{flex: 1}}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                       <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {barber.name} 
                          <button 
                            onClick={() => handleEditBarberName(barber.id, barber.name)} 
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#64748b' }}
                            title="Edit Name"
                          >
                            ✏️
                          </button>
                        </h3>
                        {/* --- NEW: "This is me" Button --- */}
                        <button 
                          onClick={() => handleSetAsOwner(barber.id)}
                          style={{ background: shopData?.ownerBarberId === barber.id ? '#10b981' : '#f1f5f9', color: shopData?.ownerBarberId === barber.id ? 'white' : '#64748b', border: 'none', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', transition: '0.2s' }}
                        >
                          {shopData?.ownerBarberId === barber.id ? '✓ This is you' : '🙋‍♂️ This is me'}
                        </button>
                      </div>
                      <div style={{display: 'flex', gap: '10px', marginTop: '10px'}}>
                        <button className="text-btn" onClick={() => openServiceModal(barber.id)}>{t.addService}</button>
                        <label className="text-btn" style={{cursor: 'pointer'}}>
                          {t.addPortfolioPhoto}
                          <input type="file" accept="image/*" style={{display: 'none'}} onChange={(e) => handlePortfolioUpload(barber.id, e)} />
                        </label>
                      </div>
                    </div>
                 </div>
                  
                  {barber.portfolio && barber.portfolio.length > 0 && (
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', overflowX: 'auto', paddingBottom: '10px' }}>
                      {barber.portfolio.map((img, i) => (
                        <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                          <img src={img} alt="Portfolio preview" style={{ width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                          <button 
                            onClick={() => handleDeletePortfolioPhoto(barber.id, i)}
                            style={{ position: 'absolute', top: '-5px', right: '-5px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: '18px', height: '18px', cursor: 'pointer', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >✕</button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="admin-services-list">
                   {barber.services && barber.services.map((svc, idx) => (
                      <div key={idx} className="admin-service-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: '#f8fafc', borderRadius: '8px', marginBottom: '8px' }}>
                        <div>
                          <span style={{ fontWeight: 'bold' }}>{svc.name}</span> <span style={{ color: '#64748b', fontSize: '12px' }}>({svc.duration})</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <span className="price-tag" style={{ fontWeight: 'bold', color: '#3b82f6' }}>{svc.price}</span>
                          
                          {/* Edit & Delete Buttons */}
                          <button onClick={() => openServiceModal(barber.id, idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }} title="Edit Service">✏️</button>
                          <button onClick={() => handleDeleteService(barber.id, idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#ef4444' }} title="Delete Service">🗑️</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* =========================================
            MY SCHEDULE TAB
            ========================================= */}
        {activeTab === 'my-schedule' && (
          <div className="admin-section">
            <h2 style={{ marginBottom: '20px' }}>My Personal Schedule</h2>
            
            {!shopData?.ownerBarberId ? (
              <div style={{ background: '#eff6ff', padding: '30px', borderRadius: '16px', border: '1px solid #bfdbfe', textAlign: 'center' }}>
                <div style={{ fontSize: '40px', marginBottom: '15px' }}>🙋‍♂️</div>
                <h3 style={{ color: '#1e3a8a', margin: '0 0 10px 0' }}>Which profile is yours?</h3>
                <p style={{ color: '#3b82f6', marginBottom: '20px' }}>You haven't told us which professional you are yet. Go to the Team tab and click "This is me" next to your name to unlock your schedule.</p>
                <button onClick={() => setActiveTab('team')} className="premium-book-btn" style={{ width: 'auto', padding: '10px 20px' }}>Go to Team Tab</button>
              </div>
            ) : (
              <div>
                {/* Find the owner's name and filter their appointments */}
                {(() => {
                  const myProfile = barbers.find(b => b.id === shopData.ownerBarberId);
                  const myApps = allAppointments.filter(app => app.barberName === myProfile?.name).sort((a, b) => new Date(`${a.date} ${a.time}`) - new Date(`${b.date} ${b.time}`));

                  if (!myProfile) return <p>Profile not found.</p>;

                  return (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px', background: '#f8fafc', padding: '15px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <img src={myProfile.image || 'https://via.placeholder.com/50'} alt="me" style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover' }} />
                        <div>
                          <strong style={{ display: 'block', fontSize: '16px', color: '#0f172a' }}>{myProfile.name}</strong>
                          <span style={{ fontSize: '13px', color: '#64748b' }}>Manage your portfolio and upcoming bookings.</span>
                        </div>
                      </div>

                      {/* --- NEW: OWNER PORTFOLIO MANAGEMENT --- */}
                      <div style={{ marginBottom: '40px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                          <h3 style={{ margin: 0, fontSize: '18px', color: '#0f172a' }}>My Portfolio</h3>
                          <label style={{ cursor: 'pointer', background: '#eff6ff', padding: '8px 15px', borderRadius: '8px', color: '#3b82f6', border: '1px solid #bfdbfe', fontWeight: 'bold', fontSize: '13px', transition: '0.2s' }}>
                            + Add Photo
                            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handlePortfolioUpload(myProfile.id, e)} />
                          </label>
                        </div>
                        
                        {myProfile.portfolio && myProfile.portfolio.length > 0 ? (
                          <div style={{ display: 'flex', gap: '15px', overflowX: 'auto', paddingBottom: '10px' }}>
                            {myProfile.portfolio.map((img, i) => (
                              <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                                <img src={img} alt="My cut" style={{ width: '100px', height: '100px', borderRadius: '12px', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                                <button 
                                  onClick={() => handleDeletePortfolioPhoto(myProfile.id, i)}
                                  style={{ position: 'absolute', top: '-8px', right: '-8px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.2)' }}
                                >✕</button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p style={{ color: '#64748b', fontSize: '14px', margin: 0, background: 'white', padding: '20px', borderRadius: '12px', border: '1px dashed #cbd5e1', textAlign: 'center' }}>
                            You haven't uploaded any photos yet. Add some to show up in the Community Feed!
                          </p>
                        )}
                      </div>

                     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3 style={{ margin: 0, fontSize: '18px', color: '#0f172a' }}>Daily Calendar</h3>
                        <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="premium-date-picker" />
                      </div>

                      <div className="schedule-timeline">
                        {(() => {
                          const todayApps = myApps.filter(app => app.date === selectedDate);
                          const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                          const todayHours = shopData?.schedule?.[days[new Date(selectedDate).getDay()]];
                          
                          if (!todayHours || todayHours.closed) return <p style={{ color: '#ef4444' }}>Shop is closed on this date.</p>;

                          const slots = [];
                          let currentTime = todayHours.open;
                          while (currentTime < todayHours.close) {
                            slots.push(currentTime);
                            let [hours, mins] = currentTime.split(':').map(Number);
                            mins += 30; if (mins >= 60) { hours += 1; mins -= 60; }
                            currentTime = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
                          }

                          return slots.map(time => {
                            const bookedApp = todayApps.find(a => a.time === time);
                            if (bookedApp) return (
                              <div key={time} className="client-row booked">
                                <span className="time">{time}</span>
                               <div className="client-details"><span className="client-name">{bookedApp.clientName}</span><span className="service" style={{ marginTop: '2px', color: '#3b82f6', fontWeight: 'bold' }}>📞 {bookedApp.clientPhone || 'No phone provided'}</span><span className="service" style={{ marginTop: '2px' }}>{bookedApp.serviceName} • {bookedApp.price}</span></div>
                                <span className="status active">Confirmed</span>
                              </div>
                            );
                            return (
                              <div key={time} className="client-row free-slot">
                                <span className="time">{time}</span><span className="service empty">Available Slot</span>
                                <button className="premium-login-btn small" onClick={async () => {
                                  const clientName = window.prompt("Enter Walk-in Client Name:");
                                  if (!clientName) return;
                                  const serviceName = window.prompt("Enter Service Name (e.g. Haircut):");
                                  try {
                                    await addDoc(collection(db, "appointments"), { clientId: "walk-in", clientName: clientName, shopId: user.uid, shopName: shopData.shopName, barberName: myProfile.name, serviceName: serviceName || "Walk-in Service", price: "TBD", duration: "30m", date: selectedDate, time: time, status: 'active', createdAt: new Date() });
                                    alert("Walk-in added! Refresh the page to see it on the grid.");
                                  } catch (e) { alert("Error adding walk-in: " + e.message); }
                                }}>+ Add Walk-in</button>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </main>

      {/* SERVICE MODAL */}
      {isServiceModalOpen && (
        <div className="login-modal-overlay" onClick={() => setIsServiceModalOpen(false)}>
          <div className="login-modal-content" style={{maxWidth: '400px'}} onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setIsServiceModalOpen(false)}>✕</button>
             <h2>{editingServiceIndex !== null ? "Edit Service" : "Add New Service"}</h2>
            <form className="auth-form" onSubmit={handleSaveService}>
              <div className="input-group"><label>Service Name</label><input type="text" value={newService.name} onChange={(e) => setNewService({...newService, name: e.target.value})} required/></div>
              <div className="input-group"><label>Price</label><input type="text" value={newService.price} onChange={(e) => setNewService({...newService, price: e.target.value})} required/></div>
              <div className="input-group"><label>Duration</label><input type="text" placeholder="e.g. 30m or 1h" value={newService.duration} onChange={(e) => setNewService({...newService, duration: e.target.value})} required /></div>
              <button type="submit" className="submit-auth-btn partner" style={{marginTop: '20px'}}>Save Service</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PartnerDashboard;